import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { verifyEmailApi } from '@/lib/auth/authApi';
import { AlertCircle, Check, CircleDot, Loader2 } from 'lucide-react';

type Status = 'loading' | 'success' | 'error';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>(token ? 'loading' : 'error');
  const [errorMsg, setErrorMsg] = useState(token ? '' : 'Lien de vérification invalide ou manquant.');
  const hasVerified = useRef(false);

  useEffect(() => {
    if (!token || hasVerified.current) return;
    hasVerified.current = true;

    verifyEmailApi(token)
      .then(() => setStatus('success'))
      .catch(err => {
        setErrorMsg(err instanceof Error ? err.message : 'Lien invalide ou déjà utilisé.');
        setStatus('error');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 to-emerald-500 p-6 font-['Plus_Jakarta_Sans',sans-serif]">
      <div className='w-full max-w-md'>
        {/* Logo */}
        <div className='text-center mb-8'>
          <div className='inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4'>
            <CircleDot className='w-8 h-8 text-emerald-600' />
          </div>
          <h1 className='text-3xl font-extrabold text-white mb-2'>BoulesConnect</h1>
          <p className='text-emerald-100'>Vérification de votre adresse email</p>
        </div>

        {/* Card */}
        <div className='bg-white rounded-3xl shadow-2xl p-8 text-center'>
          {status === 'loading' && (
            <>
              <div className='w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Loader2 className='w-8 h-8 text-slate-400 animate-spin' />
              </div>
              <h2 className='text-xl font-bold text-slate-900 mb-2'>Vérification en cours…</h2>
              <p className='text-slate-500 text-sm'>Veuillez patienter.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className='w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Check className='w-8 h-8 text-emerald-600' />
              </div>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Email confirmé !</h2>
              <p className='text-slate-500 text-sm mb-6'>
                Votre adresse email a bien été vérifiée. Vous pouvez maintenant vous connecter.
              </p>
              <Link
                to={ROUTES.LOGIN}
                className='inline-flex items-center justify-center w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition shadow-lg shadow-emerald-200'
              >
                Se connecter
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <AlertCircle className='w-8 h-8 text-red-600' />
              </div>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Lien invalide</h2>
              <p className='text-slate-500 text-sm mb-6'>{errorMsg}</p>
              <Link
                to={ROUTES.LOGIN}
                className='inline-flex items-center justify-center w-full border border-slate-200 text-slate-700 font-semibold py-3 rounded-xl hover:bg-slate-50 transition'
              >
                Retour à la connexion
              </Link>
            </>
          )}
        </div>

        {/* Footer */}
        <div className='mt-8 text-center'>
          <p className='text-emerald-200 text-sm'>&copy; 2026 BoulesConnect. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
}
